import React, { useState, useRef } from 'react';
import { Wine, Star, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';

const MARGARITA_VIDEO = "https://customer-assets.emergentagent.com/job_17f7f306-0e4d-40d3-bb9a-89c30fc1d18e/artifacts/q9173t9f_Casa%20loco%20-%202.mp4";

const DRINK_PHOTOS = {
  mangoChilli: "https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/5rv2nke0_Mango%20Chilli.jpg",
  oaxacaOldFashioned: "https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/7csegdy5_Oaxaca%20Old-%20Fashioned.jpg"
};

const signatureCocktails = [
  {
    name: "Classic Margaritas",
    description: "Classic, Chilli, Coconut, Espresso, Peach, Lychee — all crafted with premium tequila",
    image: DRINK_PHOTOS.mangoChilli,
    tag: "6 Varieties"
  },
  {
    name: "Mexican Classics",
    description: "Paloma, Picante, Batanga, Michelada, Fantarita, Tequila Sunrise, Mojito & more",
    image: DRINK_PHOTOS.oaxacaOldFashioned,
    tag: "9 Cocktails"
  }
];

const MargaritaBarSection = () => {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="relative py-16 sm:py-20" style={{ backgroundColor: '#1A1918' }} data-testid="margarita-bar-section">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Star size={10} fill="#F7B638" stroke="none" />
            <Wine style={{ width: '32px', height: '32px', color: '#F7B638' }} />
            <Star size={10} fill="#F7B638" stroke="none" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ color: '#F7B638', textShadow: '3px 3px 0 #000' }} data-testid="margarita-bar-title">
            Experience the Finest Margarita Bar in Gachibowli
          </h2>
          <div className="zigzag-divider-gold max-w-xs mx-auto mb-4" />
          <p className="font-mono text-white/50 max-w-md mx-auto text-sm">
            Handcrafted cocktails with premium spirits
          </p>
        </div>

        {/* Video Section */}
        <div className="relative max-w-4xl mx-auto mb-12 overflow-hidden"
          style={{ border: '3px solid #F7B638', boxShadow: '5px 5px 0 #F7B638' }}
          data-testid="margarita-bar-video">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/ef6f91cq_Bar%20Team.jpg"
            className="w-full aspect-[21/9] object-cover"
            style={{ objectPosition: 'center 75%' }}
          >
            <source src={MARGARITA_VIDEO} type="video/mp4" />
          </video>
          <button
            onClick={toggleMute}
            className="absolute bottom-4 right-4 w-10 h-10 flex items-center justify-center text-white transition-transform hover:scale-110"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)', border: '2px solid #F7B638' }}
            data-testid="margarita-bar-mute-btn"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Signature Cocktails */}
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-center mb-8" style={{ color: '#F7B638', textShadow: '2px 2px 0 #000' }} data-testid="margaritas-title">
          Sip the Best Margaritas in India
        </h2>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          {signatureCocktails.map((cocktail, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden transition-transform duration-300 hover:translate-x-[-3px] hover:translate-y-[-3px]"
              data-testid={`signature-cocktail-${idx}`}
              style={{ border: '3px solid #F7B638', boxShadow: '5px 5px 0 #F7B638' }}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={cocktail.image}
                  alt={cocktail.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block text-xs font-mono font-bold px-2 py-1 uppercase tracking-wider" style={{ backgroundColor: '#EE2535', color: 'white' }}>
                    {cocktail.tag}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-white mb-2" style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.5)' }}>
                  {cocktail.name}
                </h3>
                <p className="font-mono text-white/70 text-sm">{cocktail.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA to full menu */}
        <div className="text-center">
          <Link
            to="/drinks-menu"
            className="inline-flex items-center gap-3 px-8 py-4 font-display tracking-wider text-lg transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
            data-testid="view-full-menu-btn"
            style={{ backgroundColor: '#EE2535', color: 'white', border: '3px solid #F7B638', boxShadow: '4px 4px 0 #F7B638' }}
          >
            VIEW FULL DRINKS MENU
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MargaritaBarSection;
